import React, { useCallback, useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import { VIDEO_EVENTS } from 'Sp/Angular/Events/Client/Gallery';
import { useResizeObserver } from 'ts/common/hooks/useResizeObserver';
import useVideoKeyboard from './useVideoKeyboard';
import { applyQuality, buildQualityList } from './utils/videoQualityUtils';

interface Options {
    url: string;
    /** Defaults to HLS, how full videos are served; the preview clip is a progressive MP4. */
    mimeType?: string;
    thumbnailUrl?: string;
    onClose?: () => void;
    /** Client gallery event bus, injected via react2angular. When present, the player pauses on
     * VIDEO_EVENTS.STOP — the lightbox filmstrip keeps navigated-away videos mounted, so the
     * unmount cleanup below never fires on next/prev and can't be relied on to stop playback. */
    spEventBus?: SpAngularJs.ISpEventBus;
    fill?: boolean;
}

const HLS_MIME_TYPE = 'application/x-mpegURL';

interface INaturalSize {
    width: number;
    height: number;
}

const useVideoPlayer = ({
    url,
    mimeType = HLS_MIME_TYPE,
    thumbnailUrl,
    onClose,
    spEventBus,
    fill = false
}: Options) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<ReturnType<typeof videojs> | null>(null);
    const hasStartedRef = useRef(false);
    const isEndedRef = useRef(false);
    const isFocusedRef = useRef(false);
    const isFirstMountRef = useRef(true);
    // Set for the duration of the play/pause "nudge" in handleSeek below, so the play/pause
    // handlers just below can tell it apart from a real, user-initiated play/pause and skip
    // updating hasStarted/isPlaying — scrubbing the timeline shouldn't look like the video
    // "started playing".
    const isNudgingRef = useRef(false);

    const [hasStarted, setHasStarted] = useState(false);
    const [isEnded, setIsEnded] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [quality, setQuality] = useState('Auto');
    const [qualities, setQualities] = useState<string[]>(['Auto']);
    // Only populated/observed in `fill` mode — see the contain-fit effect below.
    const [containerSize, setContainerSize] = useState<INaturalSize | null>(null);
    const [videoNaturalSize, setVideoNaturalSize] = useState<INaturalSize | null>(null);

    useResizeObserver({
        containerRef: fill ? containerRef : null,
        callback: (element) =>
            setContainerSize({ width: element.clientWidth, height: element.clientHeight })
    });

    useEffect(() => {
        if (!videoRef.current) {
            return;
        }

        const videoEl = document.createElement('video');
        videoEl.className = 'video-js';
        videoRef.current.appendChild(videoEl);

        const player = videojs(videoEl, {
            controls: false,
            // No `aspectRatio` option — video.js auto-detects the true ratio from the loaded
            // video instead of forcing one.
            fluid: !fill,
            poster: thumbnailUrl || undefined,
            sources: [{ src: url, type: mimeType }],
            // 'none' left the HLS tech with no manifest/seekable range until the first play(),
            // so a seek (e.g. clicking the trim timeline) before ever playing was silently
            // dropped — only taking effect once playback had loaded it for real. 'metadata' is
            // enough to fetch the manifest/seekable range up front without buffering segments.
            preload: 'metadata',
            userActions: { click: false }
        });

        player.on('play', () => {
            if (isNudgingRef.current) {
                return;
            }

            hasStartedRef.current = true;
            isEndedRef.current = false;
            setHasStarted(true);
            setIsPlaying(true);
            setIsEnded(false);
        });

        player.on('pause', () => {
            if (isNudgingRef.current) {
                return;
            }

            setIsPlaying(false);
        });

        player.on('ended', () => {
            isEndedRef.current = true;
            setIsPlaying(false);
            setIsEnded(true);
        });

        player.on('timeupdate', () => setCurrentTime(player.currentTime() ?? 0));

        player.on('loadedmetadata', () => {
            setDuration(player.duration() ?? 0);
            setQualities(buildQualityList(player));

            if (fill) {
                const videoWidth = player.videoWidth();
                const videoHeight = player.videoHeight();

                if (videoWidth && videoHeight) {
                    setVideoNaturalSize({ width: videoWidth, height: videoHeight });
                }
            }
        });

        player.on('volumechange', () => {
            setIsMuted(player.muted() ?? false);
            setVolume(player.volume() ?? 1);
        });

        playerRef.current = player;

        const handleFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === containerRef.current);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            player.dispose();
            playerRef.current = null;
        };
    }, []);

    // Pause on lightbox navigation. The filmstrip keeps navigated-away videos mounted, so the
    // unmount cleanup above doesn't fire on next/prev — this event is the only reliable stop.
    useEffect(() => {
        if (!spEventBus) {
            return;
        }

        return spEventBus.on(VIDEO_EVENTS.STOP, () => {
            playerRef.current?.pause();
        });
    }, [spEventBus]);

    useEffect(() => {
        if (isFirstMountRef.current) {
            isFirstMountRef.current = false;
            return;
        }

        if (hasStartedRef.current) {
            return;
        }

        if (playerRef.current) {
            playerRef.current.src([{ src: url, type: mimeType }]);
            playerRef.current.load();
            playerRef.current.poster(thumbnailUrl || '');
            isEndedRef.current = false;
            setHasStarted(false);
            setIsEnded(false);
            setCurrentTime(0);
            setIsPlaying(false);
            setDuration(0);
            setPlaybackSpeed(1);
            setQuality('Auto');
            setQualities(['Auto']);

            if (fill) {
                setVideoNaturalSize(null);
            }
        }
    }, [url, mimeType, thumbnailUrl, fill]);

    useEffect(() => {
        if (!fill || !containerSize || !videoNaturalSize || !playerRef.current) {
            return;
        }

        const { width: containerWidth, height: containerHeight } = containerSize;
        const { width: videoWidth, height: videoHeight } = videoNaturalSize;

        if (containerWidth <= 0 || containerHeight <= 0) {
            return;
        }

        const scale = Math.min(containerWidth / videoWidth, containerHeight / videoHeight);

        playerRef.current.width(Math.round(videoWidth * scale));
        playerRef.current.height(Math.round(videoHeight * scale));
    }, [fill, containerSize, videoNaturalSize]);

    const handleFocus = () => {
        isFocusedRef.current = true;
    };

    const handleBlur = (e: React.FocusEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            isFocusedRef.current = false;
        }
    };

    const handlePlayPause = () => {
        const p = playerRef.current;

        if (!p) {
            return;
        }

        p.paused() ? p.play() : p.pause();
    };

    const nudgeRepaint = (p: ReturnType<typeof videojs>) => {
        // HLS/MSE playback commonly leaves the displayed frame stuck at the old position after
        // a seek while paused — the browser doesn't decode/repaint the new frame until playback
        // actually resumes, which read as "the video doesn't move until you press play". A brief
        // play/pause pulse forces that repaint immediately. Guarded against overlapping pulses
        // (e.g. while dragging the timeline, which calls this repeatedly) via isNudgingRef.
        if (!p.paused() || isNudgingRef.current) {
            return;
        }

        isNudgingRef.current = true;

        Promise.resolve(p.play())
            .catch(() => undefined)
            .finally(() => {
                p.pause();
                isNudgingRef.current = false;
            });
    };

    const handleSeek = (time: number) => {
        const p = playerRef.current;

        if (!p) {
            return;
        }

        // Before metadata has loaded there's no seekable range yet for the HLS tech to seek
        // within, so currentTime() would be silently dropped — defer until it's available
        // instead of losing the seek (e.g. clicking the trim timeline right after mount, before
        // the manifest request finishes).
        if (p.readyState() === 0) {
            p.one('loadedmetadata', () => {
                p.currentTime(time);
                nudgeRepaint(p);
            });
        } else {
            p.currentTime(time);
            nudgeRepaint(p);
        }

        setCurrentTime(time);
    };

    const handleToggleMute = () => {
        const p = playerRef.current;

        if (!p) {
            return;
        }

        p.muted(!p.muted());
    };

    const handleVolumeChange = (value: number) => {
        const p = playerRef.current;

        if (!p) {
            return;
        }

        p.volume(value);

        if (value > 0) {
            p.muted(false);
        }
    };

    const handleToggleFullscreen = useCallback(() => {
        if (!containerRef.current) {
            return;
        }

        if (document.fullscreenElement === containerRef.current) {
            document.exitFullscreen();
        } else if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen();
        }
    }, []);

    useVideoKeyboard({
        playerRef,
        hasStartedRef,
        isEndedRef,
        isFocusedRef,
        onToggleFullscreen: handleToggleFullscreen,
        onClose
    });

    const handleSpeedChange = (speed: number) => {
        playerRef.current?.playbackRate(speed);
        setPlaybackSpeed(speed);
        setShowSettings(false);
    };

    const handleQualityChange = (q: string) => {
        if (playerRef.current) {
            applyQuality(playerRef.current, q);
        }

        setQuality(q);
        setShowSettings(false);
    };

    const handleReplay = () => {
        const p = playerRef.current;

        if (!p) {
            return;
        }

        hasStartedRef.current = false;
        isEndedRef.current = false;
        setIsEnded(false);
        setCurrentTime(0);
        p.currentTime(0);
        p.play();
    };

    const getCurrentTime = useCallback(() => Number(playerRef.current?.currentTime() ?? 0), []);

    const play = useCallback(() => {
        playerRef.current?.play();
    }, []);

    const pause = useCallback(() => {
        playerRef.current?.pause();
    }, []);

    const stop = useCallback(() => {
        const p = playerRef.current;

        if (!p) {
            return;
        }

        p.pause();
        p.currentTime(0);
        hasStartedRef.current = false;
        isEndedRef.current = false;
        setHasStarted(false);
        setIsEnded(false);
        setIsPlaying(false);
        setCurrentTime(0);
    }, []);

    return {
        containerRef,
        videoRef,
        hasStarted,
        isEnded,
        isPlaying,
        currentTime,
        duration,
        volume,
        isMuted,
        isFullscreen,
        showSettings,
        playbackSpeed,
        quality,
        qualities,
        handleFocus,
        handleBlur,
        handlePlayPause,
        handleSeek,
        handleToggleMute,
        handleVolumeChange,
        handleToggleFullscreen,
        handleSpeedChange,
        handleQualityChange,
        handleReplay,
        toggleSettings: () => setShowSettings((v) => !v),
        getCurrentTime,
        play,
        pause,
        stop
    };
};

export default useVideoPlayer;
